/**
 * Merge Timeline Component (PROJ-047-G)
 *
 * Visual timeline of merge events for an entity.
 * Shows chronological history of merges and rollbacks.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { httpClient } from '@/lib/http-client';
import { GitMerge, Undo2, User, Calendar, ChevronRight, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

interface TimelineEvent {
  type: 'merge' | 'rollback';
  date: string;
  transactionId: string;
  survivorId?: string;
  mergedIds?: string[];
  mergedCount?: number;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  wasThisEntitySurvivor?: boolean;
  wasThisEntityMerged?: boolean;
  reason?: string;
  rolledBackBy?: string;
}

interface MergeTimelineProps {
  entityId: string;
  entityType: 'incident' | 'person' | 'company';
  onViewTransaction?: (transactionId: string) => void;
}

export function MergeTimeline({ entityId, entityType, onViewTransaction }: MergeTimelineProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTimeline();
  }, [entityId, entityType]);

  const loadTimeline = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await httpClient.get(
        `/api/v1/merge-history/timeline/${entityType}/${entityId}`
      );

      if (response.data.success) {
        setEvents(response.data.data.events);
      } else {
        throw new Error(response.data.error || 'Failed to load timeline');
      }
    } catch (err: any) {
      console.error('Error loading timeline:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            <GitMerge className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No hay historial de fusiones para este registro</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitMerge className="h-5 w-5" />
          Timeline de Fusiones
        </CardTitle>
        <CardDescription>
          {events.length} {events.length === 1 ? 'evento' : 'eventos'} registrados
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {events.map((event, index) => (
            <div key={`${event.transactionId}-${index}`} className="relative">
              {/* Timeline connector */}
              {index < events.length - 1 && (
                <div className="absolute left-[19px] top-[40px] bottom-[-24px] w-0.5 bg-border" />
              )}

              {/* Event card */}
              <div className="flex gap-4">
                {/* Icon */}
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                    event.type === 'merge'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-orange-100 text-orange-600'
                  }`}
                >
                  {event.type === 'merge' ? (
                    <GitMerge className="h-5 w-5" />
                  ) : (
                    <Undo2 className="h-5 w-5" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 space-y-2">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-semibold">
                        {event.type === 'merge' ? 'Fusión de Registros' : 'Reversión de Fusión'}
                      </h4>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(event.date), 'PPp')}
                      </p>
                    </div>
                    {event.type === 'merge' && (
                      <Badge
                        variant={event.wasThisEntitySurvivor ? 'default' : 'secondary'}
                      >
                        {event.wasThisEntitySurvivor ? 'Sobreviviente' : 'Fusionado'}
                      </Badge>
                    )}
                  </div>

                  {/* Details */}
                  {event.type === 'merge' && (
                    <div className="text-sm space-y-1">
                      {event.wasThisEntitySurvivor && (
                        <p className="text-muted-foreground">
                          Este registro absorbió <strong>{event.mergedCount}</strong>{' '}
                          {event.mergedCount === 1 ? 'registro' : 'registros'}
                        </p>
                      )}
                      {event.wasThisEntityMerged && (
                        <p className="text-muted-foreground">
                          Este registro fue fusionado en:{' '}
                          <span className="font-mono text-xs">{event.survivorId?.slice(0, 8)}...</span>
                        </p>
                      )}
                      {event.user && (
                        <p className="text-xs flex items-center gap-1 text-muted-foreground">
                          <User className="h-3 w-3" />
                          {event.user.firstName} {event.user.lastName}
                        </p>
                      )}
                    </div>
                  )}

                  {event.type === 'rollback' && event.reason && (
                    <div className="text-sm space-y-1">
                      <p className="text-muted-foreground">
                        <strong>Razón:</strong> {event.reason}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  {onViewTransaction && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewTransaction(event.transactionId)}
                      className="mt-2"
                    >
                      Ver Detalle
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
